import type { RegulatoryScheme } from '../../unit-meta/index.js';
import {
  type IssueMetadataCorsiaActual,
  SustainabilityTier,
} from '../../unit-meta/index.js';
import {
  getActualGHGIntensityAirportDelivery,
  getValue,
  tier1Schemes,
  tierASchemes,
  tierCFeedStock,
} from '../constants.js';

export namespace CalculationsV1CorsiaActual {
  export const equations = {
    [SustainabilityTier.A]: ({
      greenhouseGas,
    }: IssueMetadataCorsiaActual) => {
      return (
        greenhouseGas.inducedLandUseChange
        + greenhouseGas.production
        + greenhouseGas.conditioning
        + greenhouseGas.processing
        + greenhouseGas.transportation
        + greenhouseGas.conversionProcesses
        + greenhouseGas.fuelTransportation
        + greenhouseGas.combustion
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
        + getValue(greenhouseGas.ghgDisplacementEmissions)
        + getValue(greenhouseGas.emissionsSavingsDueToSoilCarbonSequestration)
        - getValue(greenhouseGas.emissionsSavingsFromCSS)
      );
    },

    [SustainabilityTier.B]: ({
      greenhouseGas,
    }: IssueMetadataCorsiaActual) => {
      return (
        greenhouseGas.inducedLandUseChange
        + greenhouseGas.production
        + greenhouseGas.conditioning
        + greenhouseGas.processing
        + greenhouseGas.transportation
        + greenhouseGas.conversionProcesses
        + greenhouseGas.fuelTransportation
        + greenhouseGas.combustion
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
        + getValue(greenhouseGas.emissionsSavingsDueToSoilCarbonSequestration)
        - getValue(greenhouseGas.emissionsSavingsFromCSS)
      );
    },

    [SustainabilityTier.C]: ({
      greenhouseGas,
    }: IssueMetadataCorsiaActual) => {
      return (
        greenhouseGas.inducedLandUseChange
        + greenhouseGas.production
        + greenhouseGas.conditioning
        + greenhouseGas.processing
        + greenhouseGas.transportation
        + greenhouseGas.conversionProcesses
        + greenhouseGas.fuelTransportation
        + greenhouseGas.combustion
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
        - getValue(greenhouseGas?.landfillEmissionsCredits)
        - getValue(greenhouseGas?.recyclingEmissionsCredits)
        - getValue(greenhouseGas.emissionsSavingsFromCSS)
      );
    },
  };

  const verifyTierB = (metadata: IssueMetadataCorsiaActual) => {
    const { product: { jetFuelBaselineCarbonIntensity, feedStock }, greenhouseGas: { emissionsSavingsFromCSS }, certification: { isLowILUCRiskConfirmed } } = metadata;

    const baseline = Number(jetFuelBaselineCarbonIntensity?.replace('g', ''));

    if (!(1 - (equations[SustainabilityTier.B](metadata) / baseline) >= 0.6)) {
      return SustainabilityTier.C;
    }

    if (!feedStock?.wasCertifiedAsProductionResidue) {
      if (isLowILUCRiskConfirmed) {
        return SustainabilityTier.B;
      }
      return SustainabilityTier.C;
    }

    if (!emissionsSavingsFromCSS) {
      return SustainabilityTier.B;
    }

    if (feedStock?.wasCertifiedAsProductionResidue) {
      return SustainabilityTier.B;
    }

    if (isLowILUCRiskConfirmed) {
      return SustainabilityTier.B;
    }

    if (!(1 - (equations[SustainabilityTier.B](metadata) + emissionsSavingsFromCSS / baseline) >= 0.6)) {
      return SustainabilityTier.B;
    }

    return SustainabilityTier.C;
  };

  export const calculateSustainabilityTierForCorsiaActual = (
    metadata: IssueMetadataCorsiaActual,
  ) => {
    const { product: { feedStock, conversionProcess, jetFuelBaselineCarbonIntensity }, certification: { scheme, isLowILUCRiskConfirmed, isLowDisplacementRiskConfirmed }, batch: { regulatorySchemesDeclaration }, greenhouseGas: { ghgDisplacementEmissions } } = metadata;
    const baseline = Number(jetFuelBaselineCarbonIntensity?.replace('g', ''));

    if (feedStock?.name && tierCFeedStock.includes(feedStock.name)) {
      return SustainabilityTier.C;
    }

    if (conversionProcess === 'ptl' && !tierASchemes.includes(scheme)) {
      return SustainabilityTier.C;
    }

    if (!isLowILUCRiskConfirmed) {
      return verifyTierB(metadata);
    }

    if (!feedStock?.wasCertifiedAsProductionResidue && regulatorySchemesDeclaration.some((x: RegulatoryScheme) =>
      tier1Schemes.includes(x),
    )) {
      return SustainabilityTier.C;
    }

    if (!ghgDisplacementEmissions) {
      return verifyTierB(metadata);
    }

    if (!isLowDisplacementRiskConfirmed) {
      return verifyTierB(metadata);
    }

    if (regulatorySchemesDeclaration.some((x: RegulatoryScheme) =>
      tier1Schemes.includes(x),
    )) {
      return SustainabilityTier.C;
    }

    if (!(1 - (equations[SustainabilityTier.A](metadata) / baseline) >= 0.6)) {
      return verifyTierB(metadata);
    }

    return SustainabilityTier.A;
  };

}
